import type { TickerBet } from '../domain'

/**
 * Demo rows.
 *
 * Deterministic shape and never shown to an authenticated session — the demo
 * login has no token, so there is nothing to read. Statuses are real
 * `orders.orders` values so the demo and a live session render the same words.
 *
 * Timestamps are relative to "now" so Bet Ticker Previous’s default last-7-days
 * window still includes settled rows (fixed August 2026 dates aged out of that
 * window and left the shared Pager unmounted on empty).
 */
function isoDaysAgo(days: number, hours = 12, minutes = 0, seconds = 0): string {
  const d = new Date()
  d.setUTCHours(hours, minutes, seconds, 0)
  d.setUTCDate(d.getUTCDate() - days)
  return d.toISOString()
}

export type DemoTickerBet = TickerBet & { stake: number; status: string }

/** Demo catalog — stake/status kept for mock filters only; stripped before wire. */
export const DEMO_TICKER_BETS: DemoTickerBet[] = [
  {
    id: 'tb-001',
    placedAt: isoDaysAgo(0, 5, 12, 0),
    matchedAt: isoDaysAgo(0, 5, 12, 1),
    memberId: 'm-ravi',
    memberUsername: 'ravi_07',
    source: 'sportsbook',
    sport: 'Cricket',
    eventName: 'India v Australia',
    marketId: '1.244001',
    marketName: 'Match Odds',
    selection: 'India',
    side: 'BACK',
    currency: 'INR',
    stake: 25000,
    odds: 1.92,
    oddsPlaced: 1.92,
    oddsMatched: 1.92,
    matchedAmount: 25000,
    unmatchedAmount: 0,
    multiplier: 1,
    status: 'MATCHED',
    clientIp: '203.0.113.10',
    doubtfull: false,
    watchlist: false,
    isFlagged: false,
    reason: '',
    isBetfairFlagged: false,
  },
  {
    id: 'tb-002',
    placedAt: isoDaysAgo(0, 5, 14, 22),
    matchedAt: null,
    memberId: 'm-amit',
    memberUsername: 'amit.k',
    source: 'sportsbook',
    sport: 'Cricket',
    eventName: 'India v Australia',
    marketId: '1.244001',
    marketName: 'Match Odds',
    selection: 'Australia',
    side: 'LAY',
    currency: 'INR',
    stake: 8000,
    odds: 2.14,
    oddsPlaced: 2.14,
    oddsMatched: 2.14,
    matchedAmount: 3000,
    unmatchedAmount: 5000,
    multiplier: 1,
    status: 'PARTIALLY_MATCHED',
    clientIp: '203.0.113.11',
    doubtfull: false,
    watchlist: false,
    isFlagged: false,
    reason: '',
    isBetfairFlagged: false,
  },
  {
    id: 'tb-003',
    placedAt: isoDaysAgo(0, 4, 48, 10),
    matchedAt: isoDaysAgo(0, 4, 48, 11),
    memberId: 'm-sunil',
    memberUsername: 'sunil.b',
    source: 'sportsbook',
    sport: 'Soccer',
    eventName: 'Chelsea v Arsenal',
    marketId: '1.244310',
    marketName: 'Over/Under 2.5 Goals',
    selection: 'Under 2.5',
    side: 'BACK',
    currency: 'INR',
    stake: 15000,
    odds: 1.78,
    oddsPlaced: 1.78,
    oddsMatched: 1.78,
    matchedAmount: 15000,
    unmatchedAmount: 0,
    multiplier: 1.05,
    status: 'EXECUTION_COMPLETE',
    clientIp: '203.0.113.19',
    doubtfull: false,
    watchlist: false,
    isFlagged: false,
    reason: '',
    isBetfairFlagged: false,
  },
  {
    id: 'tb-ls-001',
    placedAt: isoDaysAgo(0, 5, 20, 0),
    matchedAt: isoDaysAgo(0, 5, 20, 1),
    memberId: 'm-ls',
    memberUsername: 'ls_player',
    source: 'sportsbook',
    sport: 'Cricket',
    eventName: 'Zimbabwe 20 Overs',
    marketId: '1.ls001',
    // Detector needs "Overs Line" (not bare "Linesession … Overs").
    marketName: '1st Innings 20 Overs Line',
    selection: 'Runs',
    side: 'BACK',
    currency: 'POINTS',
    stake: 100,
    odds: 116.5,
    oddsPlaced: 116.5,
    oddsMatched: 2,
    matchedAmount: 100,
    unmatchedAmount: 0,
    multiplier: 1,
    status: 'MATCHED',
    clientIp: '203.0.113.50',
    doubtfull: false,
    watchlist: false,
    isFlagged: false,
    reason: '',
    isBetfairFlagged: false,
  },
  {
    id: 'tb-101',
    placedAt: isoDaysAgo(2, 14, 22, 0),
    matchedAt: isoDaysAgo(2, 14, 22, 2),
    memberId: 'm-amit',
    memberUsername: 'amit.k',
    source: 'sportsbook',
    sport: 'Cricket',
    eventName: 'England v South Africa',
    marketId: '1.243110',
    marketName: 'Match Odds',
    selection: 'England',
    side: 'BACK',
    currency: 'INR',
    stake: 40000,
    odds: 1.66,
    oddsPlaced: 1.66,
    oddsMatched: 1.66,
    matchedAmount: 40000,
    unmatchedAmount: 0,
    multiplier: 1,
    status: 'WINNER_DECLARED',
    clientIp: '203.0.113.11',
    doubtfull: false,
    watchlist: false,
    isFlagged: false,
    reason: '',
    isBetfairFlagged: false,
  },
  {
    id: 'tb-102',
    placedAt: isoDaysAgo(3, 9, 5, 0),
    matchedAt: isoDaysAgo(3, 9, 5, 3),
    memberId: 'm-sunil',
    memberUsername: 'sunil.b',
    source: 'sportsbook',
    sport: 'Soccer',
    eventName: 'Milan v Inter',
    marketId: '1.242990',
    marketName: 'Both Teams to Score',
    selection: 'Yes',
    side: 'BACK',
    currency: 'INR',
    stake: 6000,
    odds: 1.72,
    oddsPlaced: 1.72,
    oddsMatched: 1.72,
    matchedAmount: 6000,
    unmatchedAmount: 0,
    multiplier: 1,
    status: 'CANCELLED',
    clientIp: '203.0.113.19',
    doubtfull: false,
    watchlist: false,
    isFlagged: false,
    reason: '',
    isBetfairFlagged: false,
  },
  {
    id: 'tb-premium-001',
    placedAt: isoDaysAgo(0, 5, 30, 0),
    matchedAt: isoDaysAgo(0, 5, 30, 1),
    memberId: 'm-ravi',
    memberUsername: 'ravi_07',
    source: 'premium-bm',
    sport: 'Cricket',
    eventName: 'India v Australia',
    marketId: '1.244001#premium',
    marketName: 'Premium Bookmaker',
    selection: 'India',
    side: 'BACK',
    currency: 'INR',
    stake: 12000,
    odds: 1.88,
    oddsPlaced: 1.88,
    oddsMatched: 1.88,
    matchedAmount: 12000,
    unmatchedAmount: 0,
    multiplier: 1,
    status: 'MATCHED',
    clientIp: '203.0.113.10',
    doubtfull: false,
    watchlist: false,
    isFlagged: false,
    reason: '',
    isBetfairFlagged: false,
  },
  {
    id: 'tb-premium-101',
    placedAt: isoDaysAgo(3, 11, 0, 0),
    matchedAt: isoDaysAgo(3, 11, 0, 2),
    memberId: 'm-amit',
    memberUsername: 'amit.k',
    source: 'premium-bm',
    sport: 'Cricket',
    eventName: 'England v South Africa',
    marketId: '1.243110#premium',
    marketName: 'Premium Bookmaker',
    selection: 'England',
    side: 'LAY',
    currency: 'INR',
    stake: 5000,
    odds: 2.05,
    oddsPlaced: 2.05,
    oddsMatched: 2.05,
    matchedAmount: 5000,
    unmatchedAmount: 0,
    multiplier: 1,
    status: 'SETTLED',
    clientIp: '203.0.113.11',
    doubtfull: false,
    watchlist: false,
    isFlagged: false,
    reason: '',
    isBetfairFlagged: false,
  },
]
